import { Box, Button, Stack } from "@mui/material";
import AppIcon from "ui/components/icons/AppIcon";
import { iconClose, iconGridView, iconLanguage } from "ui/components/icons";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import {
  CommentContent,
  GlobalSearchContent,
  GlobalSearchFormattedData,
  GlobalSearchSelectOption,
  MeetingContent,
} from "data/@types/GlobalSearchData";
import { useGlobalSearch } from "data/hooks/useGlobalSearch";
import React, { useEffect } from "react";
import { Virtuoso } from "react-virtuoso";
import Theme from "ui/Theme";
import EmptySearch from "./feedback/EmptySearch";
import { NoResultsFound } from "./feedback/NoResultsFound";
import GlobalSearchSidebar from "./GlobalSearchSidebar";
import { GlobalSearchSortOptions } from "./GlobalSearchSortOptions";
import GlobalSearchActivityItem from "./result-items/GlobalSearchActivityItem";
import GlobalSearchFirmItem from "./result-items/GlobalSearchFirmItem";
import GlobalSearchMeetingItem from "./result-items/GlobalSearchMeetingItem";
import ProductStrategyItem from "./result-items/GlobalSearchProductStrategyItem";
import SectionTitle from "./result-items/GlobalSearchSectionTitle";
import { useGlobalSearchContext } from "./SearchProvider";
import Search from "./SearchText";
import { GlobalSearchCommentsTable } from "./table-view/GlobalSearchCommentsTable";
import { GlobalSearchMeetingsTable } from "./table-view/GlobalSearchMeetingsTable";

type NoResultsItem = {
  isNoResults: true;
  sectionName: string;
};

type SectionHeader = {
  isHeader: true;
  sectionName: string;
};

type PreparedDataItem = SectionHeader | NoResultsItem | GlobalSearchContent;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

function GlobalSearch() {
  const { isOpen, handleClose, search, sortByCreatedDate } =
    useGlobalSearchContext();
  const {
    data,
    isLoading,
    selectedItem,
    updateSelectedItem,
    fetchGlobalSearchList,
  } = useGlobalSearch(search, sortByCreatedDate);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [openAdvancedView, setOpenAdvancedView] = React.useState<{
    activityType: GlobalSearchSelectOption;
    openAdvancedView: boolean;
    advancedViewData: MeetingContent[] | CommentContent[];
  }>({
    activityType: selectedItem,
    openAdvancedView: false,
    advancedViewData: [],
  });

  const doOpenAdvancedView = async (option: GlobalSearchSelectOption) => {
    if (!search) {
      fetchGlobalSearchList(option);
    }
    setOpenAdvancedView({
      openAdvancedView: true,
      activityType: option,
      advancedViewData: getAdvancedViewData(option),
    });
  };

  const advancedViewActivities: GlobalSearchSelectOption[] = [
    "Meetings",
    "Comments",
  ];

  const getAdvancedViewData = (
    name = selectedItem,
  ): MeetingContent[] | CommentContent[] => {
    const section = data.find((item) => item.name === name);
    return (section?.content as MeetingContent[] | CommentContent[]) ?? [];
  };

  const isAdvancedViewEnabled =
    advancedViewActivities.includes(selectedItem) &&
    getAdvancedViewData().length > 0 &&
    !isLoading;

  const prepareDataForVirtuoso = (
    data: GlobalSearchFormattedData[],
  ): PreparedDataItem[] => {
    let preparedData: PreparedDataItem[] = [];

    data.forEach(({ name, content }) => {
      preparedData.push({ isHeader: true, sectionName: name });

      if (content && content.length > 0) {
        const sectionContentWithName = content.map((item) => ({
          ...item,
          sectionName: name,
        })) as PreparedDataItem[];

        preparedData = preparedData.concat(sectionContentWithName);
      } else {
        preparedData.push({ isNoResults: true, sectionName: name });
      }
    });

    return preparedData;
  };

  const renderItem = (item: any) => {
    if (item.isHeader) {
      return <SectionTitle title={item.sectionName} />;
    }

    if (item.isNoResults) {
      return <NoResultsFound />;
    }

    switch (item.sectionName) {
      case "Firms":
        return <GlobalSearchFirmItem firm={item} />;
      case "Products/Strategies":
        return <ProductStrategyItem productStrategy={item} />;
      case "Comments":
        return (
          <GlobalSearchActivityItem activityType="COMMENT" source={item} />
        );
      case "Meetings":
        return <GlobalSearchMeetingItem meeting={item} />;
      case "Rating Changes":
        return (
          <GlobalSearchActivityItem
            activityType="RATING_CHANGE"
            source={item}
          />
        );
      case "Issue Sheet Updates":
        return (
          <GlobalSearchActivityItem activityType="ISSUE_SHEET" source={item} />
        );
    }
  };

  const renderSearchResults = () => {
    if (!search) {
      return (
        <Grid
          size={{ xs: 12, sm: 8, md: 9, lg: 10 }}
          alignSelf="center"
          textAlign="center"
          paddingBottom="12.5rem"
        >
          <EmptySearch />
        </Grid>
      );
    }

    const resultsToRender =
      selectedItem === "All"
        ? data
        : data.filter(({ name }) => name === selectedItem);
    const preparedData = prepareDataForVirtuoso(resultsToRender);

    const shouldShowSortOptions =
      selectedItem !== "Firms" && selectedItem !== "Products/Strategies";

    return (
      <Grid
        size={{ xs: 12, sm: 8, md: 9, lg: 10 }}
        sx={{
          mt: "1rem",
          overflowY: "hidden",
          overflowX: "hidden",
          paddingTop: "0 !important",
          maxHeight: "calc(100vh - 25.5rem)",
          paddingBottom: "2rem",
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          {shouldShowSortOptions && <GlobalSearchSortOptions />}
          {advancedViewActivities.includes(selectedItem) && (
            <Button
              color="info"
              size="small"
              sx={{ mr: 2, lineHeight: 0 }}
              disabled={!isAdvancedViewEnabled}
              onClick={() =>
                setOpenAdvancedView({
                  openAdvancedView: true,
                  activityType: selectedItem,
                  advancedViewData: getAdvancedViewData(),
                })
              }
              variant="outlined"
              endIcon={<AppIcon icon={iconGridView} />}
            >
              <span style={{ marginTop: ".3rem" }}>Advanced View</span>
            </Button>
          )}
        </Stack>
        {isLoading ? (
          <div />
        ) : (
          <Virtuoso
            data={preparedData}
            itemContent={(index) => renderItem(preparedData[index])}
            style={{ height: "100%", scrollbarGutter: "stable" }}
          />
        )}
      </Grid>
    );
  };

  useEffect(() => {
    setOpenAdvancedView((prevState) => {
      return {
        activityType: selectedItem,
        openAdvancedView: prevState.openAdvancedView,
        advancedViewData: getAdvancedViewData(),
      };
    });
  }, [data]);

  return (
    <Theme paletteMode="dark">
      <Dialog
        onClose={handleClose}
        open={isOpen}
        TransitionComponent={Transition}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "common.caGrey.dark",
              opacity: "0.89 !important",
            },
          },
        }}
        PaperProps={{
          sx: {
            height: "100vh",
            borderRadius: "",
            minWidth: "95vw",
            backgroundColor: "background.globalSearch.main", // TODO: check why this color is being faded
          },
        }}
        sx={{ p: "2.4rem" }}
        disableRestoreFocus
        maxWidth="xl"
        fullWidth
        data-test-id="global-search"
      >
        <DialogTitle style={{ fontWeight: 400 }}>
          <AppIcon
            icon={iconLanguage}
            fontSize="large"
            sx={{ transform: "translateY(8px)" }}
          />{" "}
          Research Central Global Search
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.primary",
          }}
          data-test-id="gs-close-modal"
        >
          <AppIcon icon={iconClose} />
        </IconButton>
        <DialogContent
          sx={{
            py: 0,
            px: "2.4rem",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Search
              ref={searchInputRef}
              isLoading={isLoading}
              globalSearchSelectedItem={selectedItem}
              items={data}
            />
          </Box>

          {openAdvancedView.openAdvancedView ? (
            // Render correct table based on activityType
            openAdvancedView.activityType === "Meetings" ? (
              <GlobalSearchMeetingsTable
                meetingRows={
                  openAdvancedView.advancedViewData as MeetingContent[]
                }
                onBack={() =>
                  setOpenAdvancedView({
                    activityType: selectedItem,
                    openAdvancedView: false,
                    advancedViewData: [],
                  })
                }
                isLoading={isLoading}
              />
            ) : (
              <GlobalSearchCommentsTable
                commentsRows={
                  openAdvancedView.advancedViewData as CommentContent[]
                }
                onBack={() =>
                  setOpenAdvancedView({
                    activityType: selectedItem,
                    openAdvancedView: false,
                    advancedViewData: [],
                  })
                }
                isLoading={isLoading}
              />
            )
          ) : (
            <Grid container spacing={2} flex={1}>
              <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
                <GlobalSearchSidebar
                  items={data}
                  selectedItem={selectedItem}
                  setSelectedItem={updateSelectedItem}
                  openAdvancedView={doOpenAdvancedView}
                  isLoading={isLoading}
                  searchTerm={search}
                />
              </Grid>
              {renderSearchResults()}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Theme>
  );
}

export default GlobalSearch;
